import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, romeDateString } from "@/lib/api";
import { Archive, Calendar, Filter, ChevronLeft, Clock, Users, Receipt, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const COURSE_ORDER = ["antipasto", "primo", "secondo", "contorno", "pizza", "dolce", "bevanda"];

const ComandeArchiviate = () => {
    const navigate = useNavigate();
    const [date, setDate] = useState(romeDateString());
    const [tables, setTables] = useState([]);
    const [tableFilter, setTableFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState(""); // "" | "consegnato" | "chiuso"
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);

    const loadTables = async () => {
        try {
            const { data } = await api.get("/tables");
            setTables(data);
        } catch (e) { /* ignore */ }
    };

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ date });
            if (tableFilter) params.set("table_id", tableFilter);
            if (statusFilter) params.set("status", statusFilter);
            const { data } = await api.get(`/archived-orders?${params.toString()}`);
            setOrders(data);
        } catch (e) {
            toast.error("Errore nel caricamento delle comande archiviate");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadTables(); }, []);
    useEffect(() => { load(); }, [date, tableFilter, statusFilter]);

    // Raggruppa per fascia oraria + tavolo
    const groups = useMemo(() => {
        const byHour = {};
        for (const o of orders) {
            const hour = (o.hour_bucket || "??").slice(0, 2) + ":00";
            if (!byHour[hour]) byHour[hour] = [];
            byHour[hour].push(o);
        }
        return Object.entries(byHour).sort((a, b) => b[0].localeCompare(a[0]));
    }, [orders]);

    const totalDay = orders.reduce((s, o) => s + (o.total || 0), 0);
    const setQuickDate = (offset) => {
        const d = new Date();
        d.setDate(d.getDate() + offset);
        setDate(romeDateString(d));
    };

    return (
        <div className="space-y-6" data-testid="archived-orders-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <button onClick={() => navigate(-1)} className="text-elegant hover:text-brand text-sm flex items-center gap-1 mb-2">
                        <ChevronLeft className="w-4 h-4" /> Indietro
                    </button>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-1">Storico</div>
                    <h1 className="font-display text-4xl font-bold flex items-center gap-3">
                        <Archive className="w-9 h-9 text-brand" /> Comande Archiviate
                    </h1>
                    <p className="text-elegant text-sm mt-2">
                        Comande completate (tutti i piatti portati al tavolo o conto chiuso), per data e tavolo.
                    </p>
                </div>
                <div className="bg-surface border border-[#1f2c44] rounded-lg px-4 py-3">
                    <div className="text-[10px] uppercase tracking-wider text-elegant">Totale del giorno</div>
                    <div className="font-display text-2xl font-bold text-brand">€ {totalDay.toFixed(2)}</div>
                    <div className="text-[10px] text-elegant">{orders.length} {orders.length === 1 ? "comanda" : "comande"}</div>
                </div>
            </div>

            <div className="bg-surface border border-[#1f2c44] rounded-lg p-4 flex flex-wrap items-end gap-3" data-testid="archived-filters">
                <div>
                    <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block">Data</label>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setQuickDate(-1)} className="h-11 w-11 border border-[#1f2c44] hover:border-brand rounded-md flex items-center justify-center" title="Giorno precedente">
                            <ChevronLeft className="w-4 h-4" />
                        </button>
                        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                            data-testid="archived-date-input"
                            className="h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm" />
                        <button onClick={() => setQuickDate(+1)} className="h-11 w-11 border border-[#1f2c44] hover:border-brand rounded-md flex items-center justify-center" title="Giorno successivo">
                            <ChevronLeft className="w-4 h-4 rotate-180" />
                        </button>
                        <button onClick={() => setDate(romeDateString())} className="h-11 px-3 text-xs border border-[#1f2c44] hover:border-brand text-elegant hover:text-brand rounded-md">Oggi</button>
                    </div>
                </div>

                <div>
                    <label className="text-[10px] uppercase tracking-wider text-elegant mb-1 block flex items-center gap-1">
                        <Filter className="w-3 h-3" /> Tavolo
                    </label>
                    <select value={tableFilter} onChange={(e) => setTableFilter(e.target.value)}
                        data-testid="archived-table-filter"
                        className="h-11 px-3 bg-surface-elevated border border-[#1f2c44] rounded-md focus:border-brand focus:outline-none text-sm min-w-[160px]">
                        <option value="">Tutti i tavoli</option>
                        {tables.sort((a, b) => a.number - b.number).map((t) => (
                            <option key={t.id} value={t.id}>Tavolo {t.number}</option>
                        ))}
                    </select>
                </div>
                <button onClick={load} className="h-11 px-4 border border-brand text-brand hover:bg-brand-soft rounded-md flex items-center gap-1 text-sm">
                    <RotateCcw className="w-4 h-4" /> Ricarica
                </button>

                <div className="ml-auto flex items-center gap-1">
                    <button onClick={() => setStatusFilter("")} data-testid="filter-all"
                        className={`h-9 px-3 rounded text-xs uppercase tracking-wider border ${!statusFilter ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand/60"}`}>Tutte</button>
                    <button onClick={() => setStatusFilter("consegnato")} data-testid="filter-consegnato"
                        className={`h-9 px-3 rounded text-xs uppercase tracking-wider border ${statusFilter === "consegnato" ? "border-brand bg-brand/10 text-brand" : "border-[#1f2c44] text-elegant hover:border-brand/60"}`}>Solo consegnate</button>
                    <button onClick={() => setStatusFilter("chiuso")} data-testid="filter-chiuso"
                        className={`h-9 px-3 rounded text-xs uppercase tracking-wider border ${statusFilter === "chiuso" ? "border-green-500 bg-green-900/30 text-green-300" : "border-[#1f2c44] text-elegant hover:border-green-400/60"}`}>Solo pagate</button>
                </div>
            </div>

            {loading && <div className="text-elegant">Caricamento...</div>}
            {!loading && orders.length === 0 && (
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-12 text-center text-elegant">
                    Nessuna comanda archiviata per il giorno selezionato.
                </div>
            )}

            <div className="space-y-6">
                {groups.map(([hour, ords]) => (
                    <div key={hour} data-testid={`archived-hour-${hour}`}>
                        <div className="flex items-center gap-2 mb-2 text-brand">
                            <Clock className="w-4 h-4" />
                            <div className="font-display text-lg font-bold">{hour}</div>
                            <div className="text-xs text-elegant">· {ords.length} {ords.length === 1 ? "comanda" : "comande"}</div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {ords.map((o) => {
                                const isOpen = expandedId === o.id;
                                return (
                                    <div key={o.id} className="bg-surface border border-[#1f2c44] rounded-lg overflow-hidden" data-testid={`archived-order-${o.id}`}>
                                        <button
                                            onClick={() => setExpandedId(isOpen ? null : o.id)}
                                            className="w-full px-4 py-3 bg-surface-elevated hover:bg-[#0e1626] flex items-center justify-between text-left"
                                        >
                                            <div>
                                                <div className="text-[10px] uppercase tracking-wider text-brand">Tavolo</div>
                                                <div className="font-display text-2xl font-bold">{o.table_number}</div>
                                                <div className="text-[10px] text-elegant mt-1">
                                                    {o.opened_local ? new Date(o.opened_local).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : "—"}
                                                    · {o.items?.length || 0} piatti
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <div className="font-display text-xl font-bold text-brand">€ {(o.total || 0).toFixed(2)}</div>
                                                <div className={`text-[10px] uppercase tracking-wider mt-1 px-2 py-0.5 rounded ${
                                                    o.display_status === "chiuso"
                                                        ? "bg-green-900/30 text-green-300"
                                                        : "bg-brand/15 text-brand"
                                                }`}>
                                                    {o.display_status === "chiuso" ? "Pagata" : "Consegnata"}
                                                </div>
                                                {o.archived_local && (
                                                    <div className="text-[9px] text-elegant mt-1">
                                                        archiv. {new Date(o.archived_local).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })}
                                                    </div>
                                                )}
                                            </div>
                                        </button>
                                        {isOpen && (
                                            <div className="p-3 border-t border-[#1f2c44] space-y-1.5">
                                                {(o.items || []).slice().sort((a, b) => COURSE_ORDER.indexOf(a.course) - COURSE_ORDER.indexOf(b.course)).map((it) => (
                                                    <div key={it.id} className="flex justify-between items-center text-sm py-1.5 border-b border-[#162033] last:border-0">
                                                        <div className="flex-1">
                                                            <span className="text-brand mr-2 font-bold">×{it.quantity}</span>
                                                            {it.name}
                                                            {it.notes && <span className="text-xs italic text-amber-300 block">📝 {it.notes}</span>}
                                                        </div>
                                                        <div className="font-display text-elegant text-xs">€ {(it.price * it.quantity).toFixed(2)}</div>
                                                    </div>
                                                ))}
                                                {o.display_status === "chiuso" && (
                                                    <button onClick={() => navigate(`/cassa/${o.table_id}`)}
                                                        className="mt-2 text-[11px] text-brand hover:underline flex items-center gap-1">
                                                        <Receipt className="w-3 h-3" /> Vedi scontrini
                                                    </button>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ComandeArchiviate;
